import { zodResolver } from '@hookform/resolvers/zod';
import { postTablesInventoryDevices } from '@ipfabric/api-client/client/model/devices/devices';
import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { Label } from '@ipfabric/ui-components/Label/Label';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import type { FloatingListItemContainerProps } from '@ipfabric/ui-components/List/FloatingList/FloatingList.types';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { TextAreaField } from '@ipfabric/ui-components/TextAreaField/TextAreaField';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { discoveryTasks } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { FormInputError } from 'components/FormInputError/FormInputError';
import { pipe } from 'fp-ts/lib/function';
import { type FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { generateGuid } from 'utils/guid';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { indexBy } from 'utils/object/indexBy';
import { validateMandatoryColumns } from 'utils/table/validateMandatoryColumns';
import { useDiscoverySettingsContext } from '../../composition/DiscoverySettings/discoverySettingsContext';
import {
  type DiscoveryTasksRowData,
  discoveryTasksRowDataSchema,
  missingRegexRuleMsg,
} from './DiscoveryTasksRowData';

const MAX_TEST_RESULTS = 100;

type Props = {
  editedRule: DiscoveryTasksRowData | 'new';
  onClose: () => void;
  onSubmit: (editedRule: DiscoveryTasksRowData) => void;
};

const ruleTypes = [
  { field: 'vendor', label: 'Vendor' },
  { field: 'family', label: 'Family' },
  { field: 'platform', label: 'Platform' },
  { field: 'model', label: 'Model' },
  { field: 'version', label: 'Version' },
  { field: 'sn', label: 'Serial nr.' },
] as const;

const ruleTypesByField = pipe(ruleTypes, indexBy(prop('field')));
type RuleField = (typeof ruleTypes)[number]['field'];
const discoveryTasksById = pipe(discoveryTasks, indexBy(prop('id')));

export const DisabledDiscoveryTaskEditModal: FC<Props> = (props) => {
  const appSnapshot = useSnapshotId();
  const snapshotToTestRegex =
    useDiscoverySettingsContext().snapshotId ?? appSnapshot;

  const getModalTitle = () =>
    props.editedRule === 'new' ? 'New rule' : 'Edit rule';

  const [appliedFields, setAppliedFields] = useState<RuleField[]>(() => {
    const { editedRule } = props;
    return editedRule === 'new'
      ? []
      : ruleTypes.filter((type) => !!editedRule[type.field]).map(prop('field'));
  });

  const discoveryTasksOptions = useMemo(
    () =>
      discoveryTasks.map((task) => ({
        label: task.label,
        value: task.id,
      })),
    [],
  );

  const form = useCustomForm<DiscoveryTasksRowData>({
    defaultValues:
      props.editedRule === 'new'
        ? {
            id: generateGuid(),
            family: '',
            label: '',
            model: '',
            platform: '',
            taskId: discoveryTasks[0].id,
            vendor: '',
            version: '',
            sn: '',
          }
        : props.editedRule,
    resolver: zodResolver(discoveryTasksRowDataSchema),
  });

  const handleSubmit = form.handleSubmit((values) => {
    props.onSubmit(values);
  });

  const ruleTypesToAdd = useMemo(
    () =>
      ruleTypes
        .filter((type) => !appliedFields.includes(type.field))
        .map(
          (type): FloatingListItemContainerProps => ({
            dataTestId: `disabled-tasks-${type.field}-select-item`,
            label: type.label,
            onClick: () => {
              setAppliedFields((fields) => [...fields, type.field]);
            },
          }),
        ),
    [appliedFields],
  );

  const taskId = useWatch({ control: form.control, name: 'taskId' });

  const ruleInputIdPrefix = useHtmlId('ruleinput');

  const [testResult, setTestResult] = useState<string | null>(null);
  const [testingRule, setTestingRule] = useLoadingState();
  const filtersError = (
    form.formState.errors as { filters?: { message?: string } }
  ).filters?.message;
  const hasFiltersError = filtersError === missingRegexRuleMsg;

  // Reset testResult to null when any ruleType field changes
  const ruleTypeFieldNames = ruleTypes.map((t) => t.field);
  const ruleTypeValues = useWatch({
    control: form.control,
    name: ruleTypeFieldNames,
  });
  useEffect(() => {
    setTestResult(null);
  }, ruleTypeValues);

  const testRules = () => {
    form.handleSubmit((data) => {
      withLoadingIndicator(setTestingRule)(async () => {
        if (snapshotToTestRegex === null) {
          stumble('Cannot test rules without a valid snapshot');
          return;
        }
        const requestColumns = appliedFields.filter(
          (field) => data[field] !== '',
        );

        const response = validateMandatoryColumns(
          ['hostname'],
          await postTablesInventoryDevices({
            columns: ['hostname'],
            filters: requestColumns.length
              ? {
                  and: requestColumns.map((column) => ({
                    [column]: ['reg', data[column]],
                  })),
                }
              : {},
            sort: {
              order: 'asc',
              column: 'hostname',
            },
            snapshot: snapshotToTestRegex,
          }),
        );
        setTestResult(
          `${response.data.map(prop('hostname')).slice(0, MAX_TEST_RESULTS).join('\n')}${
            response.data.length > MAX_TEST_RESULTS
              ? `\n+ ${response.data.length - MAX_TEST_RESULTS} more`
              : ''
          }`,
        );
      });
    })();
  };

  return (
    <SidePanel
      closeOnBackdropClick={false}
      onClose={props.onClose}
      title={getModalTitle()}
      footer={
        <div className='flex flex-row gap-xs'>
          <Button
            className='flex-1'
            dataTestId='disabled-discovery-tasks-save-button'
            icon='fa-floppy-disk'
            type='submit'
            variant='primary'
          >
            {props.editedRule === 'new' ? 'Add rule' : 'Save changes'}
          </Button>
          <Tooltip
            shouldShow={() => snapshotToTestRegex === null}
            content='A snapshot must be loaded to test a rule.'
          >
            {/* Wrapped because a disabled button has pointer-events: none */}
            <div className='flex flex-row flex-1'>
              <Button
                className='flex-1'
                variant='outline'
                loading={testingRule === 'LOADING'}
                disabled={snapshotToTestRegex === null}
                onClick={testRules}
              >
                Test rule
              </Button>
            </div>
          </Tooltip>
          <Button className='flex-1' variant='outline' onClick={props.onClose}>
            Close
          </Button>
        </div>
      }
      wrapContent={(content) => <form onSubmit={handleSubmit}>{content}</form>}
    >
      <div className='flex flex-col gap'>
        <Card className='p flex flex-col gap'>
          <div className='flex flex-col gap-3xs'>
            <FormSelect.Mandatory
              name='taskId'
              label='Disabled task'
              options={discoveryTasksOptions}
              control={form.control}
            />
            <div className='text-default text-sm'>
              {discoveryTasksById[taskId].description}
            </div>
          </div>
          <FormTextField
            control={form.control}
            dataTestId='disabled-discovery-tasks-rule-description'
            label='Rule description'
            name='label'
            type='text'
          />
          <div className='flex flex-col gap-xs'>
            <div className='flex flex-col gap-3xs'>
              <Label renderAs='div'>Filtering options</Label>
              <div className='text-default text-sm'>
                Rule can be limited to certain devices based on the filters
                specified here.
              </div>
            </div>

            <div className='grid grid-cols-[4rem_1fr_auto] gap-3xs items-start'>
              {appliedFields.map((field) => (
                <Fragment key={field}>
                  {/* Center the label and delete icon with the text field, even when validation error appears */}
                  <div className='h-8 flex items-center'>
                    <Label htmlFor={`${ruleInputIdPrefix}-${field}`}>
                      {ruleTypesByField[field].label}
                    </Label>
                  </div>
                  <FormTextField
                    control={form.control}
                    dataTestId={`disabled-discovery-tasks-${field}-field`}
                    name={field}
                    type='text'
                  />
                  <div className='h-8 flex items-center'>
                    <IconButton
                      variant='plain'
                      accent='negative'
                      size='small'
                      icon='fa-trash-can'
                      onClick={() => {
                        setAppliedFields((fields) =>
                          fields.filter((f) => f !== field),
                        );
                        form.setValue(field, '');
                      }}
                      type='button'
                    />
                  </div>
                </Fragment>
              ))}
            </div>
            {ruleTypesToAdd.length > 0 && (
              <FloatingList items={ruleTypesToAdd}>
                {(setRef, setOpen) => (
                  <div>
                    <Link
                      accent={hasFiltersError ? 'negative' : 'positive'}
                      data-testid='disabled-discovery-tasks-add-filter'
                      forwardRef={setRef}
                      icon='fa-plus'
                      iconPosition='start'
                      onClick={() => {
                        setOpen(true);
                      }}
                      renderAs='button'
                    >
                      Add filter
                    </Link>
                  </div>
                )}
              </FloatingList>
            )}
            <FormInputError
              className='mt-2xs'
              error={filtersError === missingRegexRuleMsg ? filtersError : ''}
            />
          </div>
        </Card>
        {testResult !== null && (
          <Card className='p flex flex-col gap'>
            <div className='text-default text-level-4 font-semibold font-heading'>
              Test result
            </div>
            {testResult === '' ? (
              'No devices match the rules.'
            ) : (
              <TextAreaField
                autogrow
                readOnly
                label='List of devices that meet the rules'
                value={testResult}
              />
            )}
          </Card>
        )}
      </div>
    </SidePanel>
  );
};
